import { BankConnectionMapper } from '@core/mapper/bankConnectionMapper';
import type { HttpClient } from '@core/model/framework/httpClient';
import { Ok } from '@core/model/framework/Result/OkResult';
import { Result } from '@core/model/framework/Result/Result';
import {
  AuthenticateToBankWithCredentialsServiceOutput,
  BankService,
  GetBankByIdServiceInput,
  GetBankByIdServiceOutput,
  GetBankConnectionServiceInput,
  GetBankConnectionServiceOutput,
  GetBankConnectionsServiceInput,
  GetBankConnectionsServiceOutput,
  GetBanksServiceOutput,
  GetWebauthJourneyStatusServiceInput,
  GetWebauthJourneyStatusServiceOutput,
  GetWebauthUrlServiceInput,
  GetWebauthUrlServiceOutput,
  SaveWebauthBankConnectionServiceInput,
  SaveWebauthBankConnectionServiceOutput,
} from '@core/model/service/bankService';
import { AuthenticateToBankWithCredentialsInput } from '@core/usecase/authenticateToBankWithCredentials';
import { SERVICE_ID } from '@di/serviceId';
import { inject, injectable } from 'inversify';
import { BankMapper } from '@core/mapper/bankMapper';
import {
  AuthenticateToBankWithCredentialsRequest,
  AuthenticateToBankWithCredentialsResponse,
} from './types/request-response/authenticateToBankWithCredentials';
import { GetBankConnectionResponse } from './types/request-response/getBankConnection';
import { GetBankConnectionsResponse } from './types/request-response/getBankConnections';
import { GetBankResponse, GetBanksResponse } from './types/request-response/getBanks';
import { GetWebauthUrlRequest, GetWebauthUrlResponse } from './types/request-response/getWebauthUrl';
import { GetWebauthJourneyStatusResponse } from './types/request-response/getWebauthJourneyStatus';
import {
  PostSaveWebauthBankConnectionRequest,
  PostSaveWebauthBankConnectionResponse,
} from './types/request-response/postSaveWebauthBankConnection';

@injectable()
export class DFBankService implements BankService {
  constructor(
    @inject(SERVICE_ID.openBankingHttpClient)
    private readonly httpClient: HttpClient,
    @inject(BankConnectionMapper)
    private readonly bankConnectionMapper: BankConnectionMapper,
    @inject(BankMapper)
    private readonly bankMapper: BankMapper
  ) {}

  async getBankById(input: GetBankByIdServiceInput): GetBankByIdServiceOutput {
    const { bankId } = input;

    const getBankResult = await this.httpClient.get<GetBankResponse>(`/banks/${bankId}`);
    const bankDto = Result.unwrap(getBankResult).data;
    const bank = this.bankMapper.toDomain(bankDto);

    return Ok(bank);
  }

  async getWebauthUrl(input: GetWebauthUrlServiceInput): GetWebauthUrlServiceOutput {
    const { userToken, bankId, form, tagging, journeyKey, mode } = input;

    // `mode` only travels when the caller has one to ask for (the relay):
    // the origin sends the historic body plus its correlation key.
    const body: GetWebauthUrlRequest = {
      tagging,
      form: form?.value,
      journeyKey,
      ...(mode != null && { mode }),
    };

    const result = await this.httpClient.post<GetWebauthUrlResponse, GetWebauthUrlRequest>(
      `/auth/webauth-url?bankId=${bankId}`,
      body,
      {
        Authorization: `Bearer ${userToken}`,
      }
    );

    const webauthUrl = Result.unwrap(result);

    return Ok({ url: webauthUrl.data.url });
  }

  async getWebauthJourneyStatus(
    input: GetWebauthJourneyStatusServiceInput
  ): GetWebauthJourneyStatusServiceOutput {
    const { userToken, journeyKey } = input;

    const result = await this.httpClient.get<GetWebauthJourneyStatusResponse>(
      `/auth/webauth-journeys/${encodeURIComponent(journeyKey)}`,
      {
        Authorization: `Bearer ${userToken}`,
      }
    );

    const { status, connectionId, state, error, destination: rawDestination } = Result.unwrap(result).data;
    // Absent from an older backend: then nobody but this page resumes.
    const destination = rawDestination === 'partner' ? 'partner' : 'widget';

    if (status === 'succeeded' && connectionId != null && state != null) {
      return Ok({ status: 'succeeded', connectionId, state, destination });
    }

    if (status === 'failed') {
      return Ok({ status: 'failed', error: error ?? 'unknown', destination });
    }

    return Ok({ status: 'pending', destination });
  }

  async getBanks(): GetBanksServiceOutput {
    const getBanksResult = await this.httpClient.get<GetBanksResponse>('/banks');
    const banks = Result.unwrap(getBanksResult).data;

    const oneyCustomBank = banks.find((bank) => {
      return bank.uid === 'UID-CUSTOM-BANK';
    });

    if (oneyCustomBank != null) {
      oneyCustomBank.form[0]?.options?.forEach((option) => {
        const splitDescription = option.name.split('=>');
        option.name = splitDescription[0]?.trim() ?? '';

        if (splitDescription.length === 1) {
          option.description = `Pas d'informations complémentaires`;
        } else {
          option.description = splitDescription[1]?.trim() ?? '';
        }
      });
    }

    return Ok(banks);
  }

  async saveWebauthBankConnection(
    input: SaveWebauthBankConnectionServiceInput
  ): SaveWebauthBankConnectionServiceOutput {
    const { webauthPopupConnectionId, webauthPopupUserToken } = input;

    const result = await this.httpClient.post<
      PostSaveWebauthBankConnectionResponse,
      PostSaveWebauthBankConnectionRequest
    >(
      `/auth/webauth-connection`,
      {
        externalConnectionId: webauthPopupConnectionId,
      },
      {
        Authorization: `Bearer ${webauthPopupUserToken}`,
      }
    );

    const bankConnectionDto = Result.unwrap(result).data;

    const bankConnection = this.bankConnectionMapper.toDomain(bankConnectionDto);

    return Ok(bankConnection);
  }

  async getBankConnections(input: GetBankConnectionsServiceInput): GetBankConnectionsServiceOutput {
    const { userId, userToken } = input;

    const result = await this.httpClient.get<GetBankConnectionsResponse>(`/users/${userId}/connections`, {
      Authorization: `Bearer ${userToken}`,
    });

    const bankConnectionsDto = Result.unwrap(result).data;
    const bankConnections = bankConnectionsDto.map((bc) => this.bankConnectionMapper.toDomain(bc));
    return Ok(bankConnections);
  }

  async getBankConnection(input: GetBankConnectionServiceInput): GetBankConnectionServiceOutput {
    const { userId, userToken, connectionId } = input;

    const result = await this.httpClient.get<GetBankConnectionResponse>(
      `/users/${userId}/connections/${connectionId}`,
      {
        Authorization: `Bearer ${userToken}`,
      }
    );

    const bankConnectionDto = Result.unwrap(result).data;
    const bankConnection = this.bankConnectionMapper.toDomain(bankConnectionDto);
    return Ok(bankConnection);
  }

  async authenticateWithCredentials(
    input: AuthenticateToBankWithCredentialsInput
  ): AuthenticateToBankWithCredentialsServiceOutput {
    const { userId, userToken, bankId, form } = input;

    const result = await this.httpClient.post<
      AuthenticateToBankWithCredentialsResponse,
      AuthenticateToBankWithCredentialsRequest
    >(
      `/auth/signin`,
      { userId, bankId, form },
      {
        Authorization: `Bearer ${userToken}`,
      }
    );

    const bankConnectionDto = Result.unwrap(result).data;
    const bankConnection = this.bankConnectionMapper.toDomain(bankConnectionDto);
    return Ok(bankConnection);
  }
}
